import {
  addDoc,
  collection,
  // connectFirestoreEmulator,
  getDocs,
  getFirestore,
  query,
  where
} from 'firebase/firestore'
import { useSentry } from '../composables/sentry'
import { validateCollectionPath } from '../utils/firestoreValidateCollectionPath'
import { app } from './FirebaseService'
import type { FirestoreDocResponse } from './FirestoreServiceInterfaces'

const { handleError } = useSentry()

export const docResponse: FirestoreDocResponse = {
  data: null,
  exists: false,
  success: false
}

const fs = getFirestore(app)
// connectFirestoreEmulator(fs, 'localhost', 8082)

export interface CreateFirestoreDoc {
  docPath: string
  data: any
}

const FirestoreService = {
  /**
   * getDocByUri is Mostly used for getting Base models and Configurator models
   * @param {object} payload
   * @param {string} payload.collection Current implementation handles only top level collections
   * @param {string} payload.uri The doc uri to look for
   * @returns {FirestoreDocResponse} Firestore response payload and document
   */
  getDocByUri: async (payload: any): Promise<FirestoreDocResponse> => {
    const {
      collection: collectionPath,
      uri,
      modelUriProp = 'model_uri'
    } = payload
    const response = { ...docResponse }
    if (!uri || !collectionPath)
      throw new Error('Doc URI and Collection required')
    try {
      const path = validateCollectionPath(collectionPath)
      if (!path.validPath)
        throw new Error('Collection path is not valid')
      const ref = await collection(fs, path.path)
      const q = query(ref, where(modelUriProp, '==', uri))
      const querySnap = await getDocs(q)
      response.success = true
      if (querySnap.empty) {
        response.exists = false
        return response
      }
      response.exists = true
      response.data = {
        ...querySnap.docs[0].data(),
        id: querySnap.docs[0].id
      }
      return response
    }
    catch (error: any) {
      handleError(error)
      return response
    }
  },
  /**
   * @param {object} payload
   * @param {string} payload.docPath collection only if createdAutoId else provide collection & doc
   * @param {any} payload.data Data payload for doc
   * @return {object}
   */
  createFirestoreDoc: async (payload: CreateFirestoreDoc) => {
    const {
      docPath,
      data
    } = payload
    if (!docPath)
      throw new Error('Document path is required')
    try {
      // If id is specified then use setDoc:merge
      const path = validateCollectionPath(docPath)
      if (!path.validPath)
        throw new Error('Collection path is not valid')

      const res = await addDoc(collection(fs, path.path), data)
      const id = res.id
      // Return the id so it can be used if needed
      return {
        success: true,
        data: {
          id
        }
      }
    }
    catch (error: any) {
      handleError(error)
      return {
        success: false
      }
    }
  },
}

export default FirestoreService
